// frontend/app/CollectionBuilderScreen.js
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigation, useRoute } from "@react-navigation/native";
import { Modal, TextInput } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useAuth } from "./AuthContext";
import { canUseTeacherTools } from "./roles";
import { FASTAPI_BASE_URL } from "./config";

import { View, Text, TouchableOpacity, FlatList, ScrollView, StyleSheet, Alert, ActivityIndicator, Platform, useWindowDimensions, Switch } from "react-native";
import TopicSelectionModal from "./TopicSelectionModal";
// console.log("[VIS] CollectionBuilderScreen module loaded");

import {
  apiCreateCollection,
  apiListCollections,
  apiTemplateFromTopic,
  apiCloneFromTopic,
  apiPatchItemSelections,
  apiGetCollectionResolved,
  apiRenameCollection,
  apiDeleteCollection, 
  apiUpdateVisibility,
  apiListShares,
  apiAddShares,
  apiRemoveShare,
  apiAssignSameDomain,
  apiPublishCollection,
  apiUnpublishCollection,
  apiGetTeacherClasses,
  apiShareCollectionWithClass,
  apiListCollectionSharedClasses,
  apiRemoveCollectionClassShare,
  apiDeleteCollectionItem,
} from "./collections";
import { useTranslation } from "./i18n/useTranslation";
import { useNotification } from "./NotificationContext";

// Chip component for scope picker (matching test creation UI)
function Chip({ active, label, onPress, variant = "default", isDarkMode }) {
  const dk = isDarkMode;
  const THEME_LIGHT = {
    grade:   { bg: "#eef2ff", border: "#c7d2fe", text: "#1e3a8a", dot: "#6366f1" },
    subject: { bg: "#ecfeff", border: "#a5f3fc", text: "#155e75", dot: "#06b6d4" },
    chapter: { bg: "#f5f3ff", border: "#ddd6fe", text: "#5b21b6", dot: "#8b5cf6" },
    topic:   { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534", dot: "#22c55e" },
    default: { bg: "#f8fafc", border: "#e2e8f0", text: "#0f172a", dot: "#94a3b8" },
  };
  const THEME_DARK = {
    grade:   { bg: "rgba(99,102,241,0.15)", border: "rgba(99,102,241,0.4)", text: "#a5b4fc", dot: "#818cf8" },
    subject: { bg: "rgba(6,182,212,0.15)", border: "rgba(6,182,212,0.4)", text: "#67e8f9", dot: "#22d3ee" },
    chapter: { bg: "rgba(139,92,246,0.15)", border: "rgba(139,92,246,0.4)", text: "#c4b5fd", dot: "#a78bfa" },
    topic:   { bg: "rgba(34,197,94,0.15)", border: "rgba(34,197,94,0.4)", text: "#86efac", dot: "#4ade80" },
    default: { bg: "#1e293b", border: "rgba(148,163,184,0.2)", text: "#f1f5f9", dot: "#94a3b8" },
  };
  const c = (dk ? THEME_DARK : THEME_LIGHT)[variant] || (dk ? THEME_DARK : THEME_LIGHT).default;
  return (
    <TouchableOpacity
      onPress={onPress}
      style={{
        paddingVertical: 6, paddingHorizontal: 10, borderRadius: 999, margin: 4,
        borderWidth: 1, borderColor: active ? c.dot : c.border,
        backgroundColor: active ? c.bg : "transparent",
        flexDirection: "row", alignItems: "center",
      }}
    >
      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c.dot, marginRight: 6, opacity: active ? 1 : 0.8 }} />
      <Text style={{ fontSize: 12, opacity: 0.95, color: c.text }}>{label}</Text>
    </TouchableOpacity>
  );
}

// VisibilityPill component for collection sharing
function VisibilityPillCollection({ visLocal, isDarkMode, t }) {
  const dk = isDarkMode || false;
  const labelMap = {
    private: t("Private"),
    org: t("School/Org"),
    public: t("Public"),
  };
  const colorMap = {
    private: {
      bg: dk ? "rgba(239,68,68,0.15)" : "#fef3f2",
      border: dk ? "rgba(239,68,68,0.4)" : "#f79481",
      text: dk ? "#fca5a5" : "#991b1b",
    },
    org: {
      bg: dk ? "rgba(16,185,129,0.15)" : "#ecfdf5",
      border: dk ? "rgba(16,185,129,0.4)" : "#6ee7b7",
      text: dk ? "#6ee7b7" : "#065f46",
    },
    public: {
      bg: dk ? "rgba(59,130,246,0.15)" : "#f0f9ff",
      border: dk ? "rgba(59,130,246,0.4)" : "#7dd3fc",
      text: dk ? "#93c5fd" : "#075985",
    },
  };
  const colors = colorMap[visLocal] || colorMap.private;

  return (
    <View
      style={{
        paddingVertical: 4,
        paddingHorizontal: 12,
        borderRadius: 20,
        borderWidth: 1.5,
        borderColor: colors.border,
        backgroundColor: colors.bg,
      }}
    >
      <Text style={{
        fontSize: 12,
        fontWeight: "600",
        color: colors.text,
        letterSpacing: 0.3,
      }}>
        {labelMap[visLocal] || visLocal}
      </Text>
    </View>
  );
}

// VisibilityOption component for collection sharing - icon cards
function VisibilityOptionCollection({ value, icon, label, description, visLocal, changeVisibility, savingVis, isDarkMode }) {
  const dk = isDarkMode || false;
  const isSelected = visLocal === value;
  return (
    <TouchableOpacity
      onPress={() => !savingVis && changeVisibility(value)}
      disabled={savingVis}
      style={{
        flex: 1,
        minWidth: 140,
        padding: 16,
        borderRadius: 12,
        borderWidth: 2,
        marginRight: 12,
        marginBottom: 12,
        borderColor: isSelected ? "#0d9488" : (dk ? "rgba(148,163,184,0.15)" : "#e5e7eb"),
        backgroundColor: isSelected ? (dk ? "rgba(13,148,136,0.15)" : "#f0fdfa") : (dk ? "#1e293b" : "#ffffff"),
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: isSelected ? 0.08 : 0.03,
        shadowRadius: 8,
        elevation: isSelected ? 3 : 1,
        opacity: savingVis ? 0.6 : 1,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 6 }}>
        <MaterialCommunityIcons
          name={icon}
          size={20}
          color={isSelected ? "#0d9488" : (dk ? "#64748b" : "#6b7280")}
          style={{ marginRight: 8 }}
        />
        <Text style={{
          fontSize: 14,
          fontWeight: "600",
          color: isSelected ? "#0d9488" : (dk ? "#f1f5f9" : "#374151"),
        }}>
          {label}
        </Text>
      </View>
      <Text style={{
        fontSize: 12,
        color: dk ? "#64748b" : "#6b7280",
        lineHeight: 16,
      }}>
        {description}
      </Text>
    </TouchableOpacity>
  );
}

/**
 * Props to pass in:
 * - board: "cbse" | "anatomy" | "igcse" | "nyssls"
 * - teacherEmail: string (must be a teacher for edit endpoints)
 * - ownerEmail: string (usually same as teacher)
 * - languageId: number (1 = EN)
 * - curriculum: the load_curriculum tree you already render
 */
// Accept both direct props and route.params (direct props take priority)
export default function CollectionBuilderScreen(props) {
  // console.log("[VIS] CollectionBuilderScreen render start");
  const dk = props.isDarkMode || false;
  useEffect(() => {
    console.log("[VIS] CollectionBuilderScreen mounted");
  }, []);

  const route = useRoute();
  const navigation = useNavigation();
  const { user, authToken } = useAuth();

  // Detail-panel tab state: "topics" (default) or "questions" (Knowledge Questions)
  const [detailTab, setDetailTab] = useState("topics");
  const isTeacher = user?.can_use_teacher_tools ?? canUseTeacherTools(user?.role);

  // Merge direct props with route.params (direct props take priority)
  const params = route?.params || {};
  const board = props.board || params.board || "anatomy";
  const ownerEmail = props.ownerEmail || params.ownerEmail || user?.email || "";
  const languageId = props.languageId || params.languageId || 1;
  const curriculum = props.curriculum || params.curriculum || [];

  // Compute teacherEmail after destructuring
  const teacherEmail = props.teacherEmail || params.teacherEmail || ownerEmail || user?.email || "";

  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null);
  const [loading, setLoading] = useState(false);

  // modal state
  const [pickerVisible, setPickerVisible] = useState(false);
  const [pickerTemplate, setPickerTemplate] = useState(null);
  const [pickerItemId, setPickerItemId] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  // --- Publish/Share local UI state ---
  const [shares, setShares] = useState([]);              // [{student_email,...}]
  const [sharesLoading, setSharesLoading] = useState(false);
  const [shareInput, setShareInput] = useState("");      // textarea for emails
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingShare, setIsSavingShare] = useState(false);
  const [visLocal, setVisLocal] = useState(activeCollection?.visibility_scope || "private");
  const [savingVis, setSavingVis] = useState(false);

  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [isAssigningDomain, setIsAssigningDomain] = React.useState(false);
  const [isUploadingCsv, setIsUploadingCsv] = React.useState(false);
  const lastSharesKeyRef = React.useRef(""); // de-dupe guard
  // prevent overlapping fetches / loops
  const sharesLoadingRef = useRef(false);

  // layout helpers
  const { width } = useWindowDimensions();
  const twoCol = Platform.OS === "web" && width >= 900;
  const styles = getStyles(dk);
  // ---- NEW: local optimistic state + helpers ----
  const [pendingAddedBoardIds, setPendingAddedBoardIds] = useState(() => new Set());
  const [pendingRemovedBoardIds, setPendingRemovedBoardIds] = useState(() => new Set());

 const boardIdOf = (t) => Number(t?.id); // topic.id is the board_topic_id in the list

 // reverse-map: board_topic_id → CollectionItem.id (for DELETE calls)
 const boardTopicToItemId = useMemo(() => {
   const m = {};
   (activeCollection?.items || []).forEach(it => {
     if (it?.board_topic_id != null) m[Number(it.board_topic_id)] = it.id;
   });
   return m;
 }, [activeCollection?.items]);

 const isTopicAlreadyChosen = (t) => {
   const bid = boardIdOf(t);
   if (pendingRemovedBoardIds.has(bid)) return false;
   return chosenBoardTopicIds.has(bid) || pendingAddedBoardIds.has(bid);
 };


  // user option: whether to open “Customize topic” after adding
  const [customizeOnAdd, setCustomizeOnAdd] = useState(false);
  const pendingRestoreY = useRef(null);

  // collapse/expand "Publish & Share" (collapsed by default to give space)
  const [shareExpanded, setShareExpanded] = useState(false);
  const [showAdvancedShare, setShowAdvancedShare] = useState(false);

  // --- Share with Class state ---
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [sharedClasses, setSharedClasses] = useState([]);

  // remember curriculum scroll position
  const listRef = useRef(null);
  const lastScrollY = useRef(0);

  // detect topics already added to the active collection
const chosenBoardTopicIds = useMemo(() => {
  const s = new Set();
  (activeCollection?.items || []).forEach(it => {
    if (it?.board_topic_id != null) s.add(Number(it.board_topic_id));
  });
  return s;
}, [activeCollection?.items]);

  // ---- Scope picker state (drill-down like test creation) ----
  const [gradeFilter, setGradeFilter] = useState(new Set());
  const [subjectFilter, setSubjectFilter] = useState(new Set());
  const [chapterFilter, setChapterFilter] = useState(new Set());

  const levels = useMemo(() => Array.isArray(curriculum) ? curriculum : [], [curriculum]);
  const subjectsByLevel = useMemo(() => {
    const m = new Map();
    for (const lvl of levels) m.set(lvl.id, lvl.subjects || []);
    return m;
  }, [levels]);
  const chaptersBySubject = useMemo(() => {
    const m = new Map();
    for (const lvl of levels) for (const s of (lvl.subjects || [])) m.set(s.id, s.chapters || []);
    return m;
  }, [levels]);
  const topicsByChapter = useMemo(() => {
    const m = new Map();
    for (const lvl of levels) for (const s of (lvl.subjects || [])) for (const ch of (s.chapters || [])) m.set(ch.id, ch.topics || []);
    return m;
  }, [levels]);

  function toggleInSet(setter, id) {
    setter(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }

  function addAllChapterTopics(chapterId) {
    const arr = topicsByChapter.get(chapterId) || [];
    for (const t of arr) {
      if (!isTopicAlreadyChosen(t)) {
        const title = t.name || `Topic ${t.id}`;
        handleAddTopic(t.id, t.source_id, title);
      }
    }
  }

  function removeAllChapterTopics(chapterId) {
    const arr = topicsByChapter.get(chapterId) || [];
    for (const t of arr) {
      if (isTopicAlreadyChosen(t)) {
        handleRemoveTopic(t.id);
      }
    }
  }

  // Auto-select single subject when grade is picked
  useEffect(() => {
    if (gradeFilter.size === 1) {
      const gradeId = [...gradeFilter][0];
      const subjects = subjectsByLevel.get(gradeId) || [];
      if (subjects.length === 1 && !subjectFilter.has(subjects[0].id)) {
        setSubjectFilter(new Set([subjects[0].id]));
      }
    }
  }, [gradeFilter, subjectsByLevel]);

  // Auto-expand single chapter when subject is picked
  useEffect(() => {
    if (subjectFilter.size === 1) {
      const subjectId = [...subjectFilter][0];
      const chapters = chaptersBySubject.get(subjectId) || [];
      if (chapters.length === 1 && !chapterFilter.has(chapters[0].id)) {
        setChapterFilter(new Set([chapters[0].id]));
      }
    }
  }, [subjectFilter, chaptersBySubject]);
  const reloadShares = useCallback(async () => {
    if (!activeCollection?.id) {
      setShares([]);
      return;
    }
    if (sharesLoadingRef.current) return; // guard
    sharesLoadingRef.current = true;
    setSharesLoading(true);
    try {
      const list = await apiListShares({
        collection_id: activeCollection.id,
        acting_teacher_email: teacherEmail,
      });
      setShares(Array.isArray(list) ? list : []);
    } catch (e) {
      // optional: console.warn("list shares failed", e);
      setShares([]);
    } finally {
      setSharesLoading(false);
      sharesLoadingRef.current = false;
    }
  }, [activeCollection?.id, teacherEmail]);

  const reloadSharedClasses = useCallback(async () => {
    if (!activeCollection?.id) {
      setSharedClasses([]);
      return;
    }
    try {
      const list = await apiListCollectionSharedClasses(activeCollection.id);
      setSharedClasses(Array.isArray(list) ? list : []);
    } catch (e) {
      console.warn("list shared classes failed", e);
      setSharedClasses([]);
    }
  }, [activeCollection?.id]);

  const handleAssignSameDomain = useCallback(async () => {
    if (!activeCollection?.id || !teacherEmail) return;
    try {
      setIsSavingShare(true);
      const result = await apiAssignSameDomain({
        collection_id: activeCollection.id,
        acting_teacher_email: teacherEmail,
      });
      // refresh once after the assignment
      await reloadShares();
      showNotification(
        result.added
          ? t("Shared with {count} students from your school!", { count: result.added })
          : t("Shared with students from your school!"),
        "success"
      );
    } catch (e) {
      console.error("assign_same_domain failed", e);
      showNotification(t("Could not share with school."), "error");
    } finally {
      setIsSavingShare(false);
    }
  }, [activeCollection?.id, teacherEmail, reloadShares, showNotification, t]);

  async function refreshCollections() {
    setLoading(true);
    try {
      const data = await apiListCollections({ board, owner_email: ownerEmail });
      setCollections(data || []);
      if (!activeCollection && (data || []).length) {
        setActiveCollection(data[0]);
      } else if (activeCollection) {
        // refresh active object reference
        const updated = (data || []).find(c => c.id === activeCollection.id);
        if (updated) setActiveCollection(updated);
      }
    } catch (e) {
      console.error("listCollections error", e);
    //   Alert.alert("Error", "Could not load collections.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refreshCollections(); /* eslint-disable-line */ }, [board, ownerEmail]);

  // Fetch teacher's classes for "Share with Class"
  useEffect(() => {
    async function fetchClasses() {
      try {
        const classes = await apiGetTeacherClasses(teacherEmail);
        setTeacherClasses(classes || []);
      } catch (err) {
        console.warn("Failed to fetch teacher classes", err);
      }
    }
    if (teacherEmail) fetchClasses();
  }, [teacherEmail]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!activeCollection?.id) {
        setVisLocal("private");
        setShares([]);
        setSharedClasses([]);
        return;
      }
      // keep the local chip in sync
      setVisLocal(activeCollection.visibility_scope || "private");
      // fetch once for this collection id + teacher
      await reloadShares();
      await reloadSharedClasses();
    })();
    return () => {
      mounted = false;
    };
  }, [activeCollection?.id, teacherEmail]); // <-- no reloadShares here

  useEffect(() => {
    if (listRef.current && lastScrollY.current > 0) {
      requestAnimationFrame(() => {
        try {
          listRef.current.scrollToOffset({ offset: lastScrollY.current, animated: false });
        } catch {}
      });
    }
  }, [activeCollection?.id]);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    const onFocus = navigation.addListener("focus", () => {
      const saved = sessionStorage.getItem("collScrollY");
      if (saved && listRef.current) {
        const y = parseFloat(saved) || 0;
        lastScrollY.current = y;
        try { listRef.current.scrollToOffset({ offset: y, animated: false }); } catch {}
      }
    });
    const onBlur = navigation.addListener("blur", () => {
      sessionStorage.setItem("collScrollY", String(lastScrollY.current || 0));
    });
    return () => { onFocus(); onBlur(); };
  }, [navigation]);

 useEffect(() => {
   setPendingAddedBoardIds(new Set());
   setPendingRemovedBoardIds(new Set());
 }, [activeCollection?.id]);

// BDD 10/6/2025

useEffect(() => {
  console.log("[VIS][ACTIVE]", {
    id: activeCollection?.id,
    scope: activeCollection?.visibility_scope,
    published: activeCollection?.published
  });
}, [activeCollection?.id, activeCollection?.visibility_scope, activeCollection?.published]);

useEffect(() => {
  console.log("[VIS][UI] visLocal:", visLocal, "savingVis:", savingVis);
}, [visLocal, savingVis]);

  async function handleCreateCollection() {
    try {
      const ts = new Date().toLocaleString().replace(/\//g, "-").replace(/, /g, " ");
      const name = `My ${board.toUpperCase()} Collection ${ts}`;
      const created = await apiCreateCollection({ name, base_board: board, owner_email: ownerEmail, strict_mode: true });
      await refreshCollections();
      setActiveCollection(created);
    } catch (e) {
      console.error("createCollection error", e);
      if (typeof window !== "undefined" && window.alert) {
        window.alert(`${t("Error")}\n\n${t("Could not create collection.")}`);
      } else {
        Alert.alert(t("Error"), t("Could not create collection."));
      }
    }
  }

  async function handleAddTopic(board_topic_id, source_topic_id, topicTitle) {
    if (!activeCollection) {
      if (typeof window !== "undefined" && window.alert) {
        window.alert(`${t("Pick a collection")}\n\n${t("Please create or select a collection first.")}`);
      } else {
        Alert.alert(t("Pick a collection"), t("Please create or select a collection first."));
      }
      return;
    }
    try {
      console.log("Adding topic", { board_topic_id, source_topic_id }); 


      // capture current scroll Y (and schedule a restore after reload)
      const y = lastScrollY.current || 0;
      pendingRestoreY.current = y;
      // optimistic 'Added' keyed strictly by board_topic_id
      setPendingAddedBoardIds(prev => {
        const next = new Set(prev);
        next.add(Number(board_topic_id));
        return next;
      });
      // clone the topic into the collection
      const item = await apiCloneFromTopic({
        collection_id: activeCollection.id,
        board,
        board_topic_id,
        source_topic_id,
        language_id: languageId,
        acting_teacher_email: teacherEmail,
        module_title: topicTitle
      });

      // setPickerTemplate(template);
      // setPickerItemId(item.id);
      // setPickerVisible(true);
      if (customizeOnAdd) {
        // only fetch the template & open the modal if the toggle is ON
        const template = await apiTemplateFromTopic({ board, board_topic_id, source_topic_id, language_id: languageId });
        // BDD 04/28/2026
        console.log("[COLL DEBUG][CollectionBuilderScreen] template_from_topic response", {
          board,
          board_topic_id,
          source_topic_id,
          languageId,
          videosCount: template?.videos_payload?.length ?? 0,
          pwosCount: template?.pwos_payload?.length ?? 0,
          quizCount: template?.quiz_payload?.length ?? 0,
          first3Videos: (template?.videos_payload || []).slice(0, 3),
        });

        console.log("[COLL DEBUG][CollectionBuilderScreen] opening picker", {
          pickerItemId: item?.id,
          videosCount: template?.videos_payload?.length ?? 0,
          first3VideoIds: (template?.videos_payload || []).slice(0, 3).map(v => ({
            id: v?.id,
            video_id: v?.video_id,
            source_id: v?.source_id,
            tag_id: v?.tag_id,
            name: v?.name,
            is_full_video: v?.is_full_video,
          })),
        });

        setPickerTemplate(template);
        setPickerItemId(item.id);
        setPickerVisible(true);
        // restore scroll right away (no list reload here, but safe to enforce)
        requestAnimationFrame(() => {
          try { listRef.current?.scrollToOffset({ offset: y, animated: false }); } catch {}
        });

      } else {
        // fast path: no customize step — just refresh in background
        await refreshCollections();
        requestAnimationFrame(() => {
          try { listRef.current?.scrollToOffset({ offset: pendingRestoreY.current ?? y, animated: false }); } catch {}
        });
      }

    } catch (e) {
      console.error("cloneFromTopic error", e);
      Alert.alert(t("Error"), t("Could not add topic to collection."));
    // revert optimistic state on failure
    setPendingAddedBoardIds(prev => {
      const next = new Set(prev);
      next.delete(Number(board_topic_id));
      return next;
    });

    }
  }

  async function handleRemoveTopic(board_topic_id) {
    const itemId = boardTopicToItemId[Number(board_topic_id)];
    if (!itemId) return;

    // optimistic
    setPendingRemovedBoardIds(prev => {
      const next = new Set(prev);
      next.add(Number(board_topic_id));
      return next;
    });

    try {
      await apiDeleteCollectionItem({ item_id: itemId, acting_teacher_email: teacherEmail });
      await refreshCollections();
    } catch (e) {
      console.error("deleteCollectionItem error", e);
      Alert.alert(t("Error"), t("Could not remove topic from collection."));
      // revert
      setPendingRemovedBoardIds(prev => {
        const next = new Set(prev);
        next.delete(Number(board_topic_id));
        return next;
      });
    }
  }

  function handleToggleTopic(topic) {
    const already = isTopicAlreadyChosen(topic);
    const title = topic.name || `${t("Topic")} ${topic.id}`;
    if (already) {
      handleRemoveTopic(topic.id);
    } else {
      handleAddTopic(topic.id, topic.source_id, title);
    }
  }

  // async function applySelections({ videos_keep_tag_ids, pwos_keep_scene_names, quiz_keep_question_ids }) {
  //   if (!pickerItemId) { setPickerVisible(false); return; }
  //   try {
  //     await apiPatchItemSelections({
  //       item_id: pickerItemId,
  //       acting_teacher_email: teacherEmail,
  //       videos_keep_tag_ids,
  //       pwos_keep_scene_names,
  //       quiz_keep_question_ids
  //     });
  //   } catch (e) {
  //     console.error("patchItemSelections error", e);
  //     Alert.alert(t("Error"), t("Could not save selections."));
  //   } finally {
  //     setPickerVisible(false);
  //     setPickerItemId(null);
  //     setPickerTemplate(null);
  //     await refreshCollections();
  //   }
  // }
  // 04/28/2026
  async function applySelections({
    videos_keep_tag_ids,
    videos_keep_video_ids_no_tag,
    pwos_keep_scene_names,
    quiz_keep_question_ids
  }) {
    if (!pickerItemId) { setPickerVisible(false); return; }
    try {
      await apiPatchItemSelections({
        item_id: pickerItemId,
        acting_teacher_email: teacherEmail,
        videos_keep_tag_ids,
        videos_keep_video_ids_no_tag,
        pwos_keep_scene_names,
        quiz_keep_question_ids
      });
    } catch (e) {
      console.error("patchItemSelections error", e);
      Alert.alert(t("Error"), t("Could not save selections."));
    } finally {
      setPickerVisible(false);
      setPickerItemId(null);
      setPickerTemplate(null);
      await refreshCollections();
    }
  }

  function previewActive() {
    if (!activeCollection?.id) return;
    navigation.navigate("CollectionViewer", { collectionId: Number(activeCollection.id) });
  }

  function openRename() {
    if (!activeCollection) return;
    setRenameTarget(activeCollection);
    setRenameValue(activeCollection.name || "");
    setRenameOpen(true);
  }

  async function applyRename() {
    try {
      if (renameTarget?.id) {
        await apiRenameCollection(renameTarget.id, renameValue || renameTarget.name, teacherEmail);
      }
      setRenameOpen(false);
      setRenameTarget(null);
      setRenameValue("");
      await refreshCollections();
    } catch (e) {
      console.error("renameCollection error", e);
      Alert.alert(t("Error"), t("Could not rename collection."));
    }
  }


async function confirmDeleteActive() {
  if (!activeCollection?.id) return;
  // Close the modal immediately
  setConfirmOpen(false);
  try {
    await apiDeleteCollection(activeCollection.id, teacherEmail);
  } catch (e) {
    console.error("deleteCollection error", e);
    const detail = e?.response?.data?.detail;
    showNotification(
      typeof detail === "string" && detail.trim()
        ? detail
        : t("Couldn't delete the collection. Please try again."),
      "error"
    );
    return;
  }
  setActiveCollection(null);
  showNotification(t("Collection deleted"), "success");
  try {
    await refreshCollections();
  } catch (e) {
    console.warn("refreshCollections after delete failed", e);
  }
}

async function changeVisibility(nextVis) {
  const collectionId = activeCollection?.id;
  if (!collectionId) return;
  if (savingVis) return;
  if (visLocal === nextVis) return;

  setSavingVis(true);
  setVisLocal(nextVis); // optimistic

  console.log("[VIS] changeVisibility start", {
    nextVis,
    prevVisLocal: visLocal,   // note: may be stale due to async state
    id: collectionId,
    teacherEmail,
  });

  try {
    if (nextVis === "private") {
      console.log("[VIS] calling apiUnpublishCollection", { id: collectionId, teacherEmail });
      await apiUnpublishCollection(collectionId, teacherEmail);
    } else {
      console.log("[VIS] calling apiPublishCollection", { id: collectionId, nextVis, teacherEmail });
      await apiPublishCollection(collectionId, nextVis, teacherEmail);
    }

    await refreshCollections(); // pull truth from server
    console.log("[VIS] changeVisibility success");
  } catch (e) {
    console.error("[VIS] changeVisibility ERROR", e);
    showNotification(t("Could not update visibility."), "error");
    // rollback
    setVisLocal(activeCollection?.visibility_scope || "private");
  } finally {
    console.log("[VIS] changeVisibility finally");
    setSavingVis(false);
  }
}


function parseEmails(input) {
  return (input || "")
    .split(/[\s,;]+/)
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);
}
async function refreshShares(
  collectionId = activeCollection?.id,
  actingEmail = teacherEmail
) {
  if (!collectionId || !actingEmail) {
    console.warn("refreshShares skipped — missing collectionId or actingEmail", {
      collectionId,
      actingEmail,
    });
    return;
  }
  setSharesLoading(true);
  try {
    const data = await apiListShares(collectionId, actingEmail);
    setShares(Array.isArray(data) ? data : []);
  } catch (e) {
    console.error("refreshShares failed", e);
  } finally {
    setSharesLoading(false);
  }
}

async function addShares() {
  if (!activeCollection?.id) return;

  const emails = parseEmails(shareInput);
  if (!emails.length) return;
  if (isSavingShare) return;
  setIsSavingShare(true);

  try {
    await apiAddShares({
      collection_id: activeCollection.id,
      acting_teacher_email: teacherEmail,
      student_emails: emails,
    });
    setShareInput("");
    await reloadShares();
    showNotification(t("Students added to collection successfully!"), "success");

  } catch (e) {
    showNotification(t("Could not add students."), "error");
  } finally {
   setIsSavingShare(false);
  }
}

async function revokeShare(email) {
  if (!activeCollection?.id) return;
  try {
    await apiRemoveShare({
      collection_id: activeCollection.id,
      acting_teacher_email: teacherEmail,
      student_email: email,
    });
    setShares(prev => prev.filter(s => s.student_email !== email));
    showNotification(t("Student removed from collection."), "success");
  } catch (e) {
    showNotification(t("Could not remove student."), "error");
  }
}

async function shareCollectionWithClass() {
  if (!selectedClassId) {
    showNotification(t("Please select a class first."), "warning");
    return;
  }
  if (!activeCollection?.id) return;
  try {
    await apiShareCollectionWithClass(activeCollection.id, selectedClassId);
    showNotification(t("Collection shared with class successfully!"), "success");
    setSelectedClassId(null);
    await reloadSharedClasses();
  } catch (e) {
    console.error(e);
    showNotification(t("Could not share collection with class."), "error");
  }
}

async function removeCollectionClassShare(classId) {
  if (!activeCollection?.id) return;
  try {
    await apiRemoveCollectionClassShare(activeCollection.id, classId);
    setSharedClasses(prev => prev.filter(c => c.class_id !== classId));
    showNotification(t("Class removed from collection."), "success");
  } catch (e) {
    showNotification(t("Could not remove class."), "error");
  }
}

async function addSharesEmails(emails) {
  if (!emails?.length) return;
  try {
    await apiAddShares({
      collection_id: activeCollection.id,
      acting_teacher_email: teacherEmail,
      student_emails: emails,
    });
    setShareInput("");
    await reloadShares();
    showNotification(t("Students added from CSV successfully!"), "success");
  } catch (e) {
    console.error("CSV addSharesEmails failed", e);
    showNotification(t("Could not add students."), "error");
  }
}

function parseEmailsFromText(text) {
  // Simple parser: split by commas/semicolons/whitespace, validate emails, dedupe
  const tokens = String(text || "").replace(/\r/g, "\n").split(/[\s,;]+/);
  const seen = new Set();
  const out = [];
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  for (const tok of tokens) {
    const e = tok.trim().toLowerCase();
    if (!e) continue;
    if (re.test(e) && !seen.has(e)) {
      seen.add(e);
      out.push(e);
    }
  }
  return out;
}

async function handleCsvFileChange(evt) {
  try {
    const file = evt.target?.files?.[0];
    if (!file) return;
    setIsUploadingCsv(true);
    const text = await file.text();
    const emails = parseEmailsFromText(text);
    if (!emails.length) {
      showNotification(t("No emails found in CSV."), "warning");
      return;
    }
    await addSharesEmails(emails);
  } catch (e) {
    console.error("CSV upload error", e);
    showNotification(t("Could not process CSV."), "error");
  } finally {
    setIsUploadingCsv(false);
    if (evt?.target) evt.target.value = ""; // reset input
  }
}


    const VIS_LABELS = {
        private: t("Private"),
        org: t("School/Org"),
        public: t("Public"),
    };

return (
  <View style={styles.container}>
    {/* Toolbar: New, Pills, Preview, Rename, Delete */}
    <View style={styles.toolbar}>
      <TouchableOpacity style={styles.primaryBtn} onPress={handleCreateCollection}>
        <Text style={styles.primaryText}>{t("New Module")}</Text>
      </TouchableOpacity>

      <FlatList
        horizontal
        data={collections}
        keyExtractor={(c) => String(c.id)}
        renderItem={({ item }) => (
          <TouchableOpacity
            style={[
              styles.collectionPill,
              activeCollection?.id === item.id && styles.collectionPillActive
            ]}
            onPress={() => {
              if (!item?.id) return;
              if (activeCollection?.id === item.id) {
                navigation.navigate("CollectionViewer", { collectionId: Number(item.id) });
              } else {
                setActiveCollection(item);
              }
            }}
            onLongPress={() => navigation.navigate("CollectionViewer", { collectionId: Number(item.id) })}
          >
            <Text style={styles.collectionPillText}>{item.name}</Text>
          </TouchableOpacity>
        )}
        contentContainerStyle={{ gap: 8 }}
        style={{ marginLeft: 12, flexGrow: 0 }}
      />

      <TouchableOpacity
        style={[styles.secondaryBtn, !activeCollection && styles.secondaryBtnDisabled]}
        onPress={previewActive}
        disabled={!activeCollection}
      >
        <Text style={styles.secondaryText}>{t("Preview Module")}</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.secondaryBtn, !activeCollection && styles.secondaryBtnDisabled]}
        onPress={openRename}
        disabled={!activeCollection}
      >
        <Text style={styles.secondaryText}>{t("Rename")}</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.dangerBtn, !activeCollection && styles.secondaryBtnDisabled]}
        onPress={() => setConfirmOpen(true)}
        disabled={!activeCollection}
      >
      <Text style={[styles.dangerText, !activeCollection && styles.secondaryTextDisabled]}>{t("Delete")}</Text>
      </TouchableOpacity>
    </View>

    {/* Tiny summary */}
    {activeCollection ? (
      <View style={styles.summary}>
        <Text style={styles.summaryText}>
            {t("Active Module")}: <Text style={styles.summaryStrong}>{activeCollection.name}</Text>
        </Text>
      </View>
    ) : null}

    {/* Detail tab bar — only when a collection is selected and user is a teacher */}
    {activeCollection && isTeacher ? (
      <View style={styles.detailTabBar}>
        <TouchableOpacity
          style={[styles.detailTab, detailTab === "topics" && styles.detailTabActive]}
          onPress={() => setDetailTab("topics")}
          activeOpacity={0.7}
        >
          <MaterialCommunityIcons
            name="bookshelf"
            size={18}
            color={detailTab === "topics" ? (dk ? "#60a5fa" : "#0b71c7") : (dk ? "#64748b" : "#94a3b8")}
            style={{ marginRight: 6 }}
          />
          <Text style={[styles.detailTabText, detailTab === "topics" && styles.detailTabTextActive]}>
            {t("Topics")}
          </Text>
        </TouchableOpacity>
      </View>
    ) : null}

    {/* ===== "Topics" tab content ===== */}
    {detailTab === "topics" ? (
    <>
    {activeCollection ? (
     <View style={styles.publishCardNew}>
       <TouchableOpacity
         onPress={() => setShareExpanded(v => !v)}
         style={styles.publishHeaderNew}
         activeOpacity={0.8}
       >
         <View style={{ flexDirection: "row", alignItems: "center" }}>
           <MaterialCommunityIcons
             name="share-variant"
             size={22}
             color={dk ? "#2dd4bf" : "#0d9488"}
             style={{ marginRight: 10 }}
           />
           <Text style={styles.publishTitleNew}>
             {t("Publish & Share")}
           </Text>
           <View style={{ marginLeft: 12 }}>
             <VisibilityPillCollection visLocal={visLocal} isDarkMode={dk} t={t} />
           </View>
         </View>
         <MaterialCommunityIcons
           name={shareExpanded ? "chevron-up" : "chevron-down"}
           size={20}
           color={dk ? "#64748b" : "#78716c"}
         />
       </TouchableOpacity>

       {shareExpanded && (
         <View style={{ padding: 20 }}>
           {/* Visibility Options */}
           <View style={{ marginBottom: 24 }}>
             <Text style={styles.sectionLabelNew}>
               {t("Visibility").toUpperCase()}
             </Text>
             <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
               <VisibilityOptionCollection
                 value="private"
                 icon="lock"
                 label={t("Private")}
                 description={t("Only you can see this collection")}
                 visLocal={visLocal}
                 changeVisibility={changeVisibility}
                 savingVis={savingVis}
                 isDarkMode={dk}
               />
               <VisibilityOptionCollection
                 value="org"
                 icon="office-building"
                 label={t("School/Org")}
                 description={t("Anyone in your school can access")}
                 visLocal={visLocal}
                 changeVisibility={changeVisibility}
                 savingVis={savingVis}
                 isDarkMode={dk}
               />
               <VisibilityOptionCollection
                 value="public"
                 icon="earth"
                 label={t("Public")}
                 description={t("Anyone with the link can access")}
                 visLocal={visLocal}
                 changeVisibility={changeVisibility}
                 savingVis={savingVis}
                 isDarkMode={dk}
               />
             </View>
           </View>

           {/* Share with Specific Students */}
           <View style={{ marginBottom: 24 }}>
             <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
               <MaterialCommunityIcons
                 name="account-plus-outline"
                 size={18}
                 color={dk ? "#2dd4bf" : "#0d9488"}
                 style={{ marginRight: 8 }}
               />
               <Text style={styles.sectionLabelNew}>
                 {t("Share with Specific Students").toUpperCase()}
               </Text>
             </View>
             <View style={{ flexDirection: "row", gap: 8, marginBottom: 12 }}>
               <TextInput
                 placeholder={t("Enter email addresses...")}
                 placeholderTextColor={dk ? "#64748b" : "#a8a29e"}
                 value={shareInput}
                 onChangeText={setShareInput}
                 multiline
                 style={{
                   flex: 1,
                   borderWidth: 1,
                   borderColor: dk ? "rgba(148,163,184,0.15)" : "#e5e7eb",
                   borderRadius: 10,
                   padding: 12,
                   fontSize: 14,
                   color: dk ? "#f1f5f9" : "#1c1917",
                   backgroundColor: dk ? "#1e293b" : "#ffffff",
                   minHeight: 44,
                 }}
               />
               <TouchableOpacity
                 onPress={addShares}
                 disabled={isSavingShare || !shareInput.trim()}
                 style={{
                   paddingVertical: 12,
                   paddingHorizontal: 18,
                   borderRadius: 10,
                   backgroundColor: (isSavingShare || !shareInput.trim()) ? (dk ? "#334155" : "#d1d5db") : "#0d9488",
                   shadowColor: "#0d9488",
                   shadowOffset: { width: 0, height: 2 },
                   shadowOpacity: 0.15,
                   shadowRadius: 4,
                   elevation: 2,
                 }}
               >
                 <Text style={{ color: "#ffffff", fontWeight: "600", fontSize: 14 }}>
                   {isSavingShare ? t("Adding...") : t("Add")}
                 </Text>
               </TouchableOpacity>
             </View>

             {/* CSV Upload & Same Domain */}
             <View style={{ flexDirection: "row", gap: 8, marginBottom: 12 }}>
               {Platform.OS === "web" && (
                 <TouchableOpacity
                   onPress={() => document.getElementById(`csv-input-collection-${activeCollection?.id}`)?.click()}
                   style={{
                     flex: 1,
                     paddingVertical: 10,
                     paddingHorizontal: 14,
                     borderRadius: 8,
                     backgroundColor: dk ? "rgba(13,148,136,0.1)" : "#f0fdfa",
                     borderWidth: 1,
                     borderColor: dk ? "rgba(13,148,136,0.3)" : "#99f6e4",
                   }}
                 >
                   <Text style={{ fontSize: 13, fontWeight: "600", color: "#0d9488", textAlign: "center" }}>
                     {t("Upload CSV")}
                   </Text>
                   <input
                     id={`csv-input-collection-${activeCollection?.id}`}
                     type="file"
                     accept=".csv"
                     style={{ display: "none" }}
                     onChange={handleCsvFileChange}
                   />
                 </TouchableOpacity>
               )}
               <TouchableOpacity
                 onPress={handleAssignSameDomain}
                 disabled={isSavingShare || !activeCollection?.id || !teacherEmail}
                 style={{
                   flex: 1,
                   paddingVertical: 10,
                   paddingHorizontal: 14,
                   borderRadius: 8,
                   backgroundColor: dk ? "rgba(13,148,136,0.1)" : "#f0fdfa",
                   borderWidth: 1,
                   borderColor: dk ? "rgba(13,148,136,0.3)" : "#99f6e4",
                   opacity: (isSavingShare || !activeCollection?.id || !teacherEmail) ? 0.6 : 1,
                 }}
               >
                 <Text style={{ fontSize: 13, fontWeight: "600", color: "#0d9488", textAlign: "center" }}>
                   {isSavingShare ? t("Sharing...") : t("Share with School")}
                 </Text>
               </TouchableOpacity>
             </View>
           </View>

           {/* Share with Class */}
           <View style={{ marginBottom: 24 }}>
             <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
               <MaterialCommunityIcons
                 name="google-classroom"
                 size={18}
                 color={dk ? "#2dd4bf" : "#0d9488"}
                 style={{ marginRight: 8 }}
               />
               <Text style={styles.sectionLabelNew}>
                 {t("Share with Class").toUpperCase()}
               </Text>
             </View>

             {Platform.OS === "web" ? (
               <select
                 value={selectedClassId || ""}
                 onChange={(e) => setSelectedClassId(e.target.value ? Number(e.target.value) : null)}
                 style={{
                   width: "100%",
                   padding: 12,
                   borderRadius: 10,
                   border: `1px solid ${dk ? "rgba(148,163,184,0.15)" : "#e5e7eb"}`,
                   backgroundColor: dk ? "#1e293b" : "#ffffff",
                   color: dk ? "#f1f5f9" : "#1c1917",
                   fontSize: 14,
                   outline: "none",
                 }}
               >
                 <option value="">{t("Select a class")}</option>
                 {teacherClasses.map((cls) => (
                   <option key={cls.id} value={cls.id}>
                     {cls.name} ({cls.school})
                   </option>
                 ))}
               </select>
             ) : null}

             <TouchableOpacity
               onPress={shareCollectionWithClass}
               disabled={!selectedClassId}
               style={{
                 marginTop: 12,
                 paddingVertical: 10,
                 paddingHorizontal: 18,
                 borderRadius: 10,
                 backgroundColor: !selectedClassId ? (dk ? "#334155" : "#d1d5db") : "#0d9488",
                 shadowColor: "#0d9488",
                 shadowOffset: { width: 0, height: 3 },
                 shadowOpacity: 0.2,
                 shadowRadius: 6,
                 elevation: 3,
                 opacity: !selectedClassId ? 0.6 : 1,
               }}
             >
               <Text style={{
                 color: "#ffffff",
                 fontWeight: "600",
                 fontSize: 14,
                 textAlign: "center",
               }}>
                 {t("Share with Selected Class")}
               </Text>
             </TouchableOpacity>
           </View>

           {/* Shared Students List */}
           {!!shares.length && (
             <View style={{
               marginBottom: 24,
               padding: 18,
               backgroundColor: dk ? "#0f172a" : "#fafaf9",
               borderRadius: 12,
               borderWidth: 1,
               borderColor: dk ? "rgba(148,163,184,0.15)" : "#e7e5e4",
             }}>
               <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
                 <MaterialCommunityIcons
                   name="account-multiple-outline"
                   size={18}
                   color={dk ? "#2dd4bf" : "#0d9488"}
                   style={{ marginRight: 8 }}
                 />
                 <Text style={styles.sectionLabelNew}>
                   {t("Shared With")} ({shares.length})
                 </Text>
               </View>
               {sharesLoading ? (
                 <ActivityIndicator />
               ) : (
                 <FlatList
                   data={shares}
                   keyExtractor={(s, i) => s.student_email + ":" + i}
                   renderItem={({ item, index }) => (
                     <View
                       style={{
                         flexDirection: "row",
                         alignItems: "center",
                         justifyContent: "space-between",
                         paddingVertical: 10,
                         borderBottomWidth: index < shares.length - 1 ? 1 : 0,
                         borderBottomColor: dk ? "rgba(148,163,184,0.15)" : "#e7e5e4",
                       }}
                     >
                       <Text style={{ fontSize: 13, color: dk ? "#94a3b8" : "#44403c", flex: 1 }}>
                         {item.student_email}
                       </Text>
                       <TouchableOpacity onPress={() => revokeShare(item.student_email)}>
                         <Text style={{ fontSize: 12, fontWeight: "600", color: "#dc2626" }}>
                           {t("Remove")}
                         </Text>
                       </TouchableOpacity>
                     </View>
                   )}
                   style={{ maxHeight: 220 }}
                 />
               )}
             </View>
           )}
           {!shares.length && !sharesLoading && (
             <View style={{ padding: 18, backgroundColor: dk ? "#0f172a" : "#fafaf9", borderRadius: 12, marginBottom: 24 }}>
               <Text style={{ color: dk ? "#64748b" : "#78716c", textAlign: "center" }}>
                 {t("No individual students added.")}
               </Text>
             </View>
           )}

           {/* Shared Classes List */}
           {!!sharedClasses.length && (
             <View style={{
               marginBottom: 24,
               padding: 18,
               backgroundColor: dk ? "rgba(16,185,129,0.08)" : "#f0fdf4",
               borderRadius: 12,
               borderWidth: 1,
               borderColor: dk ? "rgba(16,185,129,0.2)" : "#bbf7d0",
             }}>
               <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
                 <MaterialCommunityIcons
                   name="google-classroom"
                   size={18}
                   color={dk ? "#2dd4bf" : "#0d9488"}
                   style={{ marginRight: 8 }}
                 />
                 <Text style={styles.sectionLabelNew}>
                   {t("Shared with Classes")} ({sharedClasses.length})
                 </Text>
               </View>
               {sharedClasses.map((c, idx) => (
                 <View
                   key={c.class_id}
                   style={{
                     flexDirection: "row",
                     alignItems: "center",
                     justifyContent: "space-between",
                     paddingVertical: 10,
                     borderBottomWidth: idx < sharedClasses.length - 1 ? 1 : 0,
                     borderBottomColor: dk ? "rgba(16,185,129,0.15)" : "#dcfce7",
                   }}
                 >
                   <View style={{ flex: 1 }}>
                     <Text style={{ fontSize: 13, color: dk ? "#f1f5f9" : "#1c1917", fontWeight: "600" }}>
                       {c.class_name}
                     </Text>
                     <Text style={{ fontSize: 11, color: dk ? "#64748b" : "#78716c", marginTop: 2 }}>
                       {c.school_name}
                     </Text>
                   </View>
                   <TouchableOpacity
                     onPress={() => removeCollectionClassShare(c.class_id)}
                     style={{
                       paddingVertical: 4,
                       paddingHorizontal: 10,
                       borderRadius: 6,
                       backgroundColor: dk ? "rgba(239,68,68,0.1)" : "#fef2f2",
                       borderWidth: 1,
                       borderColor: dk ? "rgba(239,68,68,0.3)" : "#fecaca",
                     }}
                   >
                     <Text style={{ fontSize: 12, color: "#dc2626", fontWeight: "600" }}>
                       {t("Remove")}
                     </Text>
                   </TouchableOpacity>
                 </View>
               ))}
             </View>
           )}
         </View>
       )}
     </View>
    ) : null}

    {/* Curriculum — scope picker (drill-down like test creation) */}
    {loading ? (
      <View style={styles.center}><ActivityIndicator /></View>
    ) : (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }}>
        {/* Customize while selecting toggle */}
        <View style={styles.inlineToggle}>
          <Text style={styles.inlineToggleLabel}>{t("Customize while selecting")}</Text>
          <Switch value={customizeOnAdd} onValueChange={setCustomizeOnAdd} />
        </View>

        <View style={{ padding: 12, borderWidth: 1, borderColor: dk ? "rgba(148,163,184,0.15)" : "#e5e7eb", borderRadius: 12, marginHorizontal: 12, marginBottom: 12, backgroundColor: dk ? "#1e293b" : "#fff" }}>
          <Text style={{ fontWeight: "600", marginBottom: 6, color: dk ? "#f1f5f9" : "#1d2b3a" }}>
            {t("Scope: {items}", { items: [t("Grades"), t("Subjects"), t("Chapters"), t("Topics")].join(" · ") })}
          </Text>

          {/* Contextual instruction banner */}
          {(gradeFilter.size === 0 || subjectFilter.size === 0 || chapterFilter.size === 0) && (
            <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 8 }}>
              <MaterialCommunityIcons name="information-outline" size={16} color={dk ? "#94a3b8" : "#64748b"} style={{ marginRight: 4 }} />
              <Text style={{ fontSize: 13, fontStyle: "italic", color: dk ? "#94a3b8" : "#64748b" }}>
                {gradeFilter.size === 0
                  ? t("Start by selecting a grade")
                  : subjectFilter.size === 0
                    ? t("Now pick a subject to see chapters")
                    : t("Tap a chapter to see its topics")}
              </Text>
            </View>
          )}

          {/* Grade chips */}
          <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
            {levels.map(l => (
              <Chip key={l.id} variant="grade" label={`${t("Grade {grade}", { grade: l.grade ?? l.id })} (${(subjectsByLevel.get(l.id) || []).length})`}
                active={gradeFilter.has(l.id)}
                onPress={() => toggleInSet(setGradeFilter, l.id)}
                isDarkMode={dk}
              />
            ))}
          </View>

          {/* Subject chips — hidden until a grade is selected */}
          {gradeFilter.size > 0 && (
            <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 6 }}>
              {levels
                .filter(l => gradeFilter.has(l.id))
                .flatMap(l => (subjectsByLevel.get(l.id) || []))
                .map(s => (
                  <Chip key={s.id} variant="subject" label={`${s.name} (${(chaptersBySubject.get(s.id) || []).length})`}
                    active={subjectFilter.has(s.id)}
                    onPress={() => toggleInSet(setSubjectFilter, s.id)}
                    isDarkMode={dk}
                  />
                ))}
            </View>
          )}

          {/* Chapter chips with expandable topics — hidden until a subject is selected */}
          {subjectFilter.size > 0 && (
          <View style={{ marginTop: 6 }}>
            {levels
              .filter(l => gradeFilter.has(l.id))
              .flatMap(l => (subjectsByLevel.get(l.id) || []))
              .filter(s => subjectFilter.has(s.id))
              .flatMap(s => (chaptersBySubject.get(s.id) || []))
              .map(ch => (
                <View key={ch.id} style={{ marginVertical: 4, marginLeft: 8 }}>
                  <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap" }}>
                    <Chip variant="chapter" label={`${ch.name} (${(topicsByChapter.get(ch.id) || []).length})`}
                      active={chapterFilter.has(ch.id)}
                      onPress={() => toggleInSet(setChapterFilter, ch.id)}
                      isDarkMode={dk}
                    />
                    {chapterFilter.has(ch.id) && (
                      <>
                        <TouchableOpacity onPress={() => addAllChapterTopics(ch.id)} style={{ marginLeft: 8 }}>
                          <Text style={{ fontSize: 12, textDecorationLine: "underline", color: dk ? "#94a3b8" : "#5b21b6" }}>{t("Select all topics")}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => removeAllChapterTopics(ch.id)} style={{ marginLeft: 8 }}>
                          <Text style={{ fontSize: 12, textDecorationLine: "underline", color: dk ? "#94a3b8" : "#5b21b6" }}>{t("Clear")}</Text>
                        </TouchableOpacity>
                      </>
                    )}
                  </View>

                  {/* Topic chips under expanded chapter */}
                  {chapterFilter.has(ch.id) && (
                    <View style={{ flexDirection: "row", flexWrap: "wrap", marginLeft: 16, marginTop: 4 }}>
                      {(topicsByChapter.get(ch.id) || []).map(tp => {
                        const already = isTopicAlreadyChosen(tp);
                        return (
                          <Chip key={tp.id} variant="topic"
                            label={tp.name || `${t("Topic")} ${tp.id}`}
                            active={already}
                            onPress={() => handleToggleTopic(tp)}
                            isDarkMode={dk}
                          />
                        );
                      })}
                    </View>
                  )}
                </View>
              ))}
          </View>
          )}

          {/* Selection summary */}
          <View style={{ marginTop: 12, padding: 10, backgroundColor: dk ? "#0f172a" : "#f9fafb", borderRadius: 8 }}>
            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
              <Text style={{ fontSize: 12, fontWeight: "600", color: dk ? "#f1f5f9" : "#1d2b3a" }}>
                {t("Selected: {count} topic(s)", { count: chosenBoardTopicIds.size })}
              </Text>
              {chosenBoardTopicIds.size > 0 && (
                <TouchableOpacity
                  onPress={() => {
                    for (const bid of chosenBoardTopicIds) {
                      handleRemoveTopic(bid);
                    }
                  }}
                >
                  <Text style={{ fontSize: 12, textDecorationLine: "underline", color: dk ? "#94a3b8" : "#64748b" }}>
                    {t("Clear all")}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
            {/* Selected topic chips with remove button */}
            {(activeCollection?.items || []).length > 0 && (
              <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 8 }}>
                {(activeCollection?.items || []).map(it => (
                  <View
                    key={it.id}
                    style={{
                      flexDirection: "row", alignItems: "center", margin: 3,
                      paddingVertical: 4, paddingLeft: 10, paddingRight: 6,
                      borderRadius: 999, borderWidth: 1,
                      borderColor: dk ? "rgba(34,197,94,0.4)" : "#bbf7d0",
                      backgroundColor: dk ? "rgba(34,197,94,0.15)" : "#f0fdf4",
                    }}
                  >
                    <Text style={{ fontSize: 11, color: dk ? "#86efac" : "#166534", marginRight: 4 }}>
                      {it.module_title || `${t("Topic")} ${it.board_topic_id}`}
                    </Text>
                    <TouchableOpacity onPress={() => handleRemoveTopic(it.board_topic_id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                      <MaterialCommunityIcons name="close-circle" size={14} color={dk ? "#86efac" : "#166534"} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    )}
    </>
    ) : null}


    {/* Topic selection (videos/pwos/quiz) */}
    <TopicSelectionModal
      visible={pickerVisible}
      onClose={() => setPickerVisible(false)}
      onApply={applySelections}
      template={pickerTemplate}
      isDarkMode={dk}
    />

    {/* Rename Modal */}
    <Modal visible={renameOpen} transparent animationType="fade">
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          <Text style={styles.modalTitle}>{t("Rename Module")}</Text>
          <TextInput
            value={renameValue}
            onChangeText={setRenameValue}
            placeholder={t("Module name")}
            placeholderTextColor={dk ? "#64748b" : "#8da3bb"}
            style={styles.modalInput}
          />
          <View style={styles.modalActions}>
            <TouchableOpacity onPress={() => { setRenameOpen(false); setRenameTarget(null); }}>
              <Text style={styles.actionCancel}>{t("Cancel")}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={applyRename}>
              <Text style={styles.actionSave}>{t("Save")}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    {/* Delete Confirmation Modal (works on web) */}
    <Modal visible={confirmOpen} transparent animationType="fade">
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          <Text style={styles.modalTitle}>{t("Delete this module?")}</Text>
            <Text style={styles.confirmText}>
            {t("This action cannot be undone.")}{" \u201C"}
            {activeCollection?.name ?? ""}{"\u201D "}
            {t("will be removed.")}
            </Text>
          <View style={styles.modalActions}>
            <TouchableOpacity onPress={() => setConfirmOpen(false)}>
              <Text style={styles.actionCancel}>{t("Cancel")}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={confirmDeleteActive}>
            <Text style={[styles.dangerText, !activeCollection && styles.secondaryTextDisabled]}>{t("Delete")}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  </View>
);

}


// ----- styles -----
const getStyles = (dk) => StyleSheet.create({
  // Teacher tools header
  teacherHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: dk ? "#1e1b4b" : "#0077b6",
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 10,
  },
  headerBtn: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    minWidth: 60,
  },
  headerBtnActive: {
    backgroundColor: "rgba(255,255,255,0.25)",
  },
  headerBtnLabel: {
    color: "#fff",
    fontSize: 10,
    marginTop: 2,
    fontWeight: "500",
  },

  advToggleRow: { marginTop: 10, marginBottom: 2 },
  advToggleText: { color: "#ddd", fontWeight: "700" },
  inlineActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    columnGap: 12,
    rowGap: 8,
    marginTop: 8,
  },
  inlineLabel: {
    color: dk ? "#94a3b8" : "#445468",
    fontWeight: "600",
  },
  smallBtn: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#1e293b",
    borderRadius: 8,
  },
  smallBtnDisabled: { opacity: 0.6 },
  smallBtnText: { color: "#fff", fontWeight: "700" },
  fileInput: { display: "inline-block", marginLeft: 8 },

  // publish/share tweaks
  publishHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 4 },
  shareCountBadge: { backgroundColor: "#1e293b", color: "#e5e7eb", paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, fontWeight: "700", fontSize: 12 },
  shareRowWrap: { gap: 16 },
  shareRowWrapWide: { flexDirection: "row" },
  shareCol: { flex: 1 },
  shareColLeft: { },
  shareColRight: { },

  // "dim" helper (used when there are no shares)
  dim: { color: dk ? "#64748b" : "#99a2ad" },

  inlineToggle: { flexDirection: "row", alignItems: "center", justifyContent: "flex-end", paddingHorizontal: 12, marginBottom: 6, gap: 8 },
  inlineToggleLabel: { color: dk ? "#94a3b8" : "#445468", fontWeight: "600" },

  container: { flex: 1, backgroundColor: dk ? "#0f172a" : "#f7f9fc" },

  header: {
    height: 56, backgroundColor: dk ? "#1e1b4b" : "#0b71c7",
    flexDirection: "row", alignItems: "center", justifyContent: "space-between",
    paddingHorizontal: 12
  },
  backBtn: { paddingVertical: 8, paddingHorizontal: 6 },
  backText: { color: "#fff", fontWeight: "700", fontSize: 16 },
  headerTitle: { color: "#fff", fontWeight: "700", fontSize: 18 },

  toolbar: {
    flexDirection: "row", alignItems: "center",
    paddingHorizontal: 12, paddingVertical: 10, gap: 8, backgroundColor: dk ? "#1e293b" : "#eef3f8"
  },
  primaryBtn: { backgroundColor: "#3a86ff", paddingVertical: 10, paddingHorizontal: 14, borderRadius: 10 },
  primaryText: { color: "white", fontWeight: "700" },
  secondaryBtn: { borderWidth: 1, borderColor: dk ? "rgba(148,163,184,0.3)" : "#88a9c8", paddingVertical: 8, paddingHorizontal: 12, borderRadius: 10 },
  secondaryBtnDisabled: { opacity: 0.5 },
  secondaryText: { color: dk ? "#60a5fa" : "#0b71c7", fontWeight: "700" },
  dangerBtn: { borderWidth: 1, borderColor: "#de5959", paddingVertical: 8, paddingHorizontal: 12, borderRadius: 10, marginLeft: 4 },
  dangerText: { color: dk ? "#f87171" : "#c11b1b", fontWeight: "700" },

  collectionPill: {
    borderWidth: 1, borderColor: dk ? "rgba(148,163,184,0.15)" : "#cfd7e3", paddingVertical: 6, paddingHorizontal: 10,
    borderRadius: 999, backgroundColor: dk ? "#1e293b" : "#fff"
  },
  collectionPillActive: { backgroundColor: dk ? "rgba(59,130,246,0.15)" : "#e9f2ff", borderColor: dk ? "rgba(59,130,246,0.4)" : "#8db9ff" },
  collectionPillText: { color: dk ? "#f1f5f9" : "#20324d", fontWeight: "600" },

  summary: { paddingHorizontal: 12, paddingBottom: 8 },
  summaryText: { color: dk ? "#94a3b8" : "#4a5a6b" },
  summaryStrong: { color: dk ? "#f1f5f9" : "#112233", fontWeight: "700" },

  center: { flex: 1, alignItems: "center", justifyContent: "center" },

  // Modals (rename / delete)
  modalOverlay: { flex: 1, backgroundColor: dk ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.45)", justifyContent: "center", padding: 24 },
  modalCard: { backgroundColor: dk ? "#1e293b" : "#fff", borderRadius: 12, padding: 16, borderWidth: 1, borderColor: dk ? "rgba(148,163,184,0.15)" : "#e6ecf2" },
  modalTitle: { fontSize: 16, fontWeight: "700", color: dk ? "#f1f5f9" : "#112233", marginBottom: 10 },
  modalInput: {
    backgroundColor: dk ? "#0f172a" : "#f4f7fb", color: dk ? "#f1f5f9" : "#112233", borderRadius: 8,
    paddingHorizontal: 10, paddingVertical: 8, borderWidth: 1, borderColor: dk ? "rgba(148,163,184,0.15)" : "#cfd7e3"
  },
  modalActions: { flexDirection: "row", justifyContent: "flex-end", marginTop: 12, gap: 16 },
  actionCancel: { color: dk ? "#94a3b8" : "#6b778c", fontWeight: "700" },
  actionSave: { color: dk ? "#60a5fa" : "#0b71c7", fontWeight: "700" },
  confirmText: { color: dk ? "#f1f5f9" : "#20324d" },
  actionDelete: { color: dk ? "#f87171" : "#c11b1b", fontWeight: "700" },
    publishCard: { backgroundColor: dk ? "#1e293b" : "#101214", borderRadius: 12, padding: 12, marginHorizontal: 12, marginBottom: 10 },
    publishTitle: { color: "#eaeaea", fontWeight: "700", fontSize: 16, marginBottom: 8 },

    segmentRow: { flexDirection: "row", gap: 8, marginBottom: 8 },
    segmentBtn: { borderWidth: 1, borderColor: dk ? "rgba(148,163,184,0.15)" : "#333", borderRadius: 10, paddingVertical: 6, paddingHorizontal: 10 },
    segmentBtnActive: { backgroundColor: "#3a86ff", borderColor: "#3a86ff" },
    segmentText: { color: "#ddd", fontWeight: "600" },
    segmentTextActive: { color: "#fff" },

    sectionHead: { color: "#ddd", fontWeight: "700", marginTop: 10, marginBottom: 6 },
    shareInput: {
    minHeight: 44,
    maxHeight: 120,
    backgroundColor: dk ? "#0f172a" : "#0c0c0c",
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#333",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    color: "#fff",
    },
    shareRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: dk ? "rgba(148,163,184,0.15)" : "#1e1f22" },
    shareEmail: { color: dk ? "#94a3b8" : "#cfd8dc", fontSize: 13 },
    removeLink: { color: "#ff7675", fontWeight: "700" },

  // New styles for redesigned publish/share card
  publishCardNew: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#f3f4f6",
    backgroundColor: dk ? "#1e293b" : "#ffffff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: dk ? 0.2 : 0.06,
    shadowRadius: 16,
    elevation: 4,
    overflow: "hidden",
    marginHorizontal: 12,
    marginBottom: 10,
  },
  publishHeaderNew: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: dk ? "#0f172a" : "#fafaf9",
    borderBottomWidth: 1,
    borderBottomColor: dk ? "rgba(148,163,184,0.15)" : "#e7e5e4",
  },
  publishTitleNew: {
    fontWeight: "700",
    fontSize: 16,
    color: dk ? "#f1f5f9" : "#1c1917",
    letterSpacing: 0.2,
  },
  sectionLabelNew: {
    fontSize: 13,
    fontWeight: "600",
    color: dk ? "#94a3b8" : "#57534e",
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 12,
  },

  // Detail tab bar (Topics / Knowledge Questions)
  detailTabBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    gap: 4,
    backgroundColor: dk ? "#1e293b" : "#f1f5f9",
    borderBottomWidth: 1,
    borderBottomColor: dk ? "rgba(148,163,184,0.15)" : "#e2e8f0",
  },
  detailTab: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: "transparent",
  },
  detailTabActive: {
    backgroundColor: dk ? "rgba(59,130,246,0.15)" : "#e0ecff",
  },
  detailTabText: {
    fontSize: 14,
    fontWeight: "600",
    color: dk ? "#64748b" : "#94a3b8",
  },
  detailTabTextActive: {
    color: dk ? "#60a5fa" : "#0b71c7",
  },

});